import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Link2, Loader2, Plus, Check } from "lucide-react";

// Admin dialog to mint & share public capture links.
// Cold callers open the resulting URL, fill the form, and the lead
// drops into the exact board+column the admin picked — no login.
export default function PublicCaptureLinks({ open, onOpenChange, boardId, columns }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState("");
  const [colKey, setColKey] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const base = (typeof window !== "undefined" ? window.location.origin : "");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-capture-links");
      setLinks(data?.links || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load links");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      load();
      setColKey(columns?.[0]?.key || "");
      setLabel("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = async () => {
    if (!label.trim()) return toast.error("Label required");
    if (!boardId || !colKey) return toast.error("Pick a column");
    setCreating(true);
    try {
      const { data } = await api.post("/lead-capture-links", {
        label: label.trim(), board_id: boardId, column_key: colKey,
      });
      toast.success("Link created");
      setLabel("");
      await load();
      // Auto-copy the fresh link
      copy(`${base}/capture/${data.token}`, data.token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally { setCreating(false); }
  };

  const copy = (url, token) => {
    try {
      navigator.clipboard.writeText(url);
      setCopied(token);
      toast.success("Copied");
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const colLabel = (key) => columns?.find((c) => c.key === key)?.label || key;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="capture-links-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 size={16} /> Public capture links
          </DialogTitle>
          <DialogDescription>
            Share these URLs with cold callers. They can submit leads
            without logging in — new leads land in the column you pick.
          </DialogDescription>
        </DialogHeader>

        {/* Create form */}
        <div className="rounded-xl border-2 border-slate-200 p-4 bg-slate-50 space-y-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
            New link
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Cold Caller Team A"
                data-testid="capture-link-label"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Drop into column</Label>
              <select
                value={colKey}
                onChange={(e) => setColKey(e.target.value)}
                className="mt-1 w-full border-2 border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="capture-link-column"
              >
                {(columns || []).map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <Button
            onClick={create}
            disabled={creating}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
            data-testid="capture-link-create-btn"
          >
            {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
            Create & copy link
          </Button>
        </div>

        {/* List */}
        <div className="space-y-2 max-h-[45vh] overflow-y-auto -mx-1 px-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold pt-1">
            Existing links {loading && <Loader2 size={10} className="inline animate-spin ml-1" />}
          </div>
          {!loading && links.length === 0 && (
            <div className="text-sm text-slate-400 py-6 text-center">
              No links yet — create one above.
            </div>
          )}
          {links.map((l) => {
            const url = `${base}/capture/${l.token}`;
            return (
              <div
                key={l.token}
                className="rounded-lg border border-slate-200 p-3 bg-white space-y-1.5"
                data-testid={`capture-link-row-${l.token}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm truncate">{l.label}</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {l.submissions || 0} submissions
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                  → {colLabel(l.column_key)}
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 flex-1 truncate">
                    {url}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(url, l.token)}
                    className="h-7 text-xs"
                    data-testid={`capture-link-copy-${l.token}`}
                  >
                    {copied === l.token
                      ? <><Check size={12} className="mr-1" /> Copied</>
                      : <><Copy size={12} className="mr-1" /> Copy</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
